import { BACKEND_URL } from "../env";
import { ApiError } from "../types/api-error";
import { User } from "../types/entities";

export interface InviteUsersData {
  emails: string[];
  role: "Admin" | "User";
  clientId: string;
  language?: "en" | "de";
}

export interface InviteUsersResult {
  success: boolean;
  results?: Array<{
    email: string;
    success: boolean;
    userId?: string;
    error?: string;
  }>;
  error?: ApiError;
}

export async function fetchUsers(
  clientId?: string
): Promise<{ users: User[]; mfaPolicy?: "off" | "totp-required" }> {
  try {
    const url = clientId
      ? `${BACKEND_URL}/users?clientId=${encodeURIComponent(clientId)}`
      : `${BACKEND_URL}/users`;
    const response = await fetch(url, {
      credentials: "include",
    });

    if (response.status >= 400) {
      throw new Error(`Fetch users failed with status ${response.status}.`);
    }

    const raw = await response.json();
    // v1: raw is User[]
    if (Array.isArray(raw)) {
      return { users: raw, mfaPolicy: undefined };
    }

    // v2: { users, mfaPolicy }
    return { users: raw.users ?? [], mfaPolicy: raw.mfaPolicy };
  } catch {
    return { users: [], mfaPolicy: undefined };
  }
}

export async function inviteUsers(
  data: InviteUsersData
): Promise<InviteUsersResult> {
  try {
    const response = await fetch(`${BACKEND_URL}/users/invite`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify(data),
    });

    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.code ??
        (response.status === 409
          ? "conflict"
          : response.status === 404
            ? "not_found"
            : undefined);
      const message =
        errorData?.message || "Failed to invite users. Please try again.";

      return {
        success: false,
        error: {
          status: response.status,
          code,
          message,
        },
      };
    }

    const result = (await response.json()) as {
      results: Array<{
        email: string;
        success: boolean;
        userId?: string;
        error?: string;
      }>;
    };

    return {
      success: true,
      results: result.results,
    };
  } catch {
    return {
      success: false,
      error: {
        message: "An unexpected error occurred. Please try again.",
      },
    };
  }
}

export async function deleteUser(
  userId: string,
  clientId?: string
): Promise<{ success: boolean; error?: ApiError }> {
  try {
    const url = clientId
      ? `${BACKEND_URL}/users/${userId}?clientId=${encodeURIComponent(clientId)}`
      : `${BACKEND_URL}/users/${userId}`;
    const response = await fetch(url, {
      method: "DELETE",
      credentials: "include",
    });

    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.code ??
        (response.status === 409
          ? "conflict"
          : response.status === 404
            ? "not_found"
            : undefined);
      const message =
        errorData?.message || "Failed to delete user. Please try again.";

      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }

    return { success: true };
  } catch {
    return {
      success: false,
      error: { message: "An unexpected error occurred. Please try again." },
    };
  }
}

export async function changeUserRole(
  userId: string,
  role: "Admin" | "User",
  clientId?: string
): Promise<{ success: boolean; error?: ApiError }> {
  try {
    const url = clientId
      ? `${BACKEND_URL}/users/${userId}/role?clientId=${encodeURIComponent(clientId)}`
      : `${BACKEND_URL}/users/${userId}/role`;
    const response = await fetch(url, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ role }),
    });

    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.code ??
        (response.status === 409
          ? "conflict"
          : response.status === 404
            ? "not_found"
            : undefined);
      const message =
        errorData?.message || "Failed to change user role. Please try again.";

      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }

    return { success: true };
  } catch {
    return {
      success: false,
      error: { message: "An unexpected error occurred. Please try again." },
    };
  }
}

export async function transferOwnership(
  userId: string,
  clientId?: string
): Promise<{ success: boolean; error?: ApiError }> {
  try {
    const url = clientId
      ? `${BACKEND_URL}/users/${userId}/transfer-ownership?clientId=${encodeURIComponent(clientId)}`
      : `${BACKEND_URL}/users/${userId}/transfer-ownership`;
    const response = await fetch(url, {
      method: "PATCH",
      credentials: "include",
    });

    if (response.status >= 400) {
      const errorData = await response.json().catch(() => ({}));
      const code =
        errorData?.code ??
        (response.status === 409
          ? "conflict"
          : response.status === 404
            ? "not_found"
            : undefined);
      const message =
        errorData?.message || "Failed to transfer ownership. Please try again.";

      return {
        success: false,
        error: { status: response.status, code, message },
      };
    }

    return { success: true };
  } catch {
    return {
      success: false,
      error: { message: "An unexpected error occurred. Please try again." },
    };
  }
}
